import { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Modal, Form, Alert, Spinner, Badge, InputGroup, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faSearch, faLock, faSyncAlt, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UserControl = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');

    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'user'
    });

    const [formErrors, setFormErrors] = useState({});

    // Fetch users
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/users`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                setUsers(response.data);
                setFilteredUsers(response.data);
                setLoading(false);
            } catch (err) {
                setError('Failed to load users. Please try again later.');
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // Filter users based on search term
    useEffect(() => {
        if (searchTerm) {
            const filtered = users.filter(user =>
                user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredUsers(filtered);
        } else {
            setFilteredUsers(users);
        }
    }, [searchTerm, users]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewUser({
            ...newUser,
            [name]: value
        });

        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: null
            });
        }
    };

    // Generate random password for create user modal
    const handleGeneratePassword = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/auth/random-password`);
            const password = response.data.password;
            setNewUser({
                ...newUser,
                password,
                confirmPassword: password
            });
            setGeneratedPassword(password);
        } catch (err) {
            setError('Failed to generate password. Please try again.');
        }
    };

    // Validate form for create user
    const validateForm = () => {
        const errors = {};

        if (!newUser.name?.trim()) {
            errors.name = "Name is required";
        }

        if (!newUser.email.trim()) {
            errors.email = 'E-mail is required';
        } else if (!/\S+@\S+\.\S+/.test(newUser.email)) {
            errors.email = "E-mail is invalid";
        }

        if (!newUser.password) {
            errors.password = "Password is required";
        } else if (newUser.password.length < 12) {
            errors.password = "Password must be at least 12 characters";
        }

        if (newUser.password !== newUser.confirmPassword) {
            errors.confirmPassword = "Passwords do not match";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Create new user
    const handleCreateUser = async () => {
        if (!validateForm()) return;

        try {
            const response = await axios.post(`${API_BASE_URL}/users`, {
                name: newUser.name,
                email: newUser.email,
                password: newUser.password,
                role: newUser.role
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });

            setUsers([...users, response.data]);
            setFilteredUsers([...users, response.data]);
            setSuccess(`User ${newUser.name} created successfully!`);
            setShowCreateModal(false);
            setNewUser({
                name: '',
                email: '',
                password: '',
                confirmPassword: '',
                role: 'user'
            });
            setGeneratedPassword('');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            if (err.response?.status === 409) {
                setError("E-mail already exists");
            } else {
                setError("Failed to create user. Please try again.");
            }
        }
    };

    // Delete user
    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            await axios.delete(`${API_BASE_URL}/users/${selectedUser.id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });

            const updatedUsers = users.filter(user => user.id !== selectedUser.id);
            setUsers(updatedUsers);
            setFilteredUsers(updatedUsers);
            setSuccess(`User ${selectedUser.name || selectedUser.email} deleted successfully!`);
            setShowDeleteModal(false);
            setSelectedUser(null);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            if (err.response?.status === 403) {
                setError("You cannot delete yourself.");
            } else {
                setError("Failed to delete user. Please try again.");
            }
        }
    };

    // Reset user password by calling the admin endpoint
    const handleResetPassword = async () => {
        if (!selectedUser) return;

        try {
            const response = await axios.get(`${API_BASE_URL}/auth/random-password`); // for users to change their own password
            const newPassword = response.data.password;

            await axios.put(
                `${API_BASE_URL}/auth/users/${selectedUser.id}/reset-password`, // for admins to reset another user’s password
                { newPassword },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                }
            );

            setNewPassword(newPassword);
            setSuccess(`Password for ${selectedUser.name || selectedUser.email} reset successfully!`);
            setShowResetPasswordModal(false);
            setTimeout(() => {
                setSuccess('');
                setNewPassword('');
                setSelectedUser(null);
            }, 15000);
        } catch (err) {
            if (err.response?.status === 403) {
                setError("Unauthorized to reset password. Admin access required.");
            } else if (err.response?.status === 400) {
                setError(err.response.data.message || "Invalid password format.");
            } else {
                setError(`Failed to reset password: ${err.message}`);
            }
        }
    };

    const confirmDelete = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    const confirmResetPassword = (user) => {
        setSelectedUser(user);
        setShowResetPasswordModal(true);
    };

    const renderRoleBadge = (role) => {
        switch(role) {
            case 'admin':
                return <Badge bg="danger">{role}</Badge>;
            default:
                return <Badge bg="primary">{role}</Badge>;
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Loading users...</span>
            </div>
        );
    }

    return (
        <>
			<div className="d-flex justify-content-end mb-4">
				<Button variant="primary" onClick={() => setShowCreateModal(true)}>
					<FontAwesomeIcon icon={faPlus} className="me-2" /> Add New User
				</Button>
			</div>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert variant="success" onClose={() => setSuccess('')} dismissible>
                    {success}
                    {newPassword && (
                        <div className="mt-2"> New password: <strong>{newPassword}</strong> </div>
                    )}
                </Alert>
            )}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">All Users</h2>
                <div style={{ width: '300px' }}>
                    <InputGroup>
                        <InputGroup.Text>
                            <FontAwesomeIcon icon={faSearch} />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </div>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                    <thead>
                        <tr>
                            <th className="text-center">#</th>
                            <th>Name</th>
                            <th>E-mail</th>
                            <th>Role</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map((user, index) => (
                                <tr key={user.id}>
                                    <td className="text-center" style={{ width: '4rem' }}>{index + 1}</td>
                                    <td>{user.name || 'N/A'}</td>
                                    <td>{user.email}</td>
                                    <td>{renderRoleBadge(user.role)}</td>
                                    <td>
                                        {new Date(user.created_at).toLocaleDateString()}
                                        <br />
                                        <small className="text-muted">
                                            {new Date(user.created_at).toLocaleTimeString()}
                                        </small>
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            title="Reset Password"
                                            onClick={() => confirmResetPassword(user)}
                                            className="me-2"
                                        >
                                            <FontAwesomeIcon icon={faSyncAlt} />
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            title="Delete User"
                                            onClick={() => confirmDelete(user)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center py-4">
                                    <div className="text-muted">No users found</div>
                                    <Button
                                        variant="link"
                                        onClick={() => setSearchTerm('')}
                                        className="mt-2"
                                    >
                                        Clear search
                                    </Button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                    Showing {filteredUsers.length} of {users.length} users
                </div>
                <div>
                    <Button variant="light" size="sm" className="me-2">Previous</Button>
                    <Button variant="light" size="sm">Next</Button>
                </div>
            </div>

            {/* Create User Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Create New User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-4">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                placeholder="Enter name"
                                value={newUser.name}
                                onChange={handleInputChange}
                                isInvalid={!!formErrors.name}
                            />
                            <Form.Control.Feedback type="invalid"> {formErrors.name} </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>E-mail address</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                placeholder="Enter e-mail"
                                value={newUser.email}
                                onChange={handleInputChange}
                                isInvalid={!!formErrors.email}
                            />
                            <Form.Control.Feedback type="invalid"> {formErrors.email} </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>
                                Password
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="password-info"> Set a password or generate one automatically. </Tooltip>
                                    }
                                >
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="ms-2 text-muted"
                                    style={{ cursor: 'pointer' }}
                                />
                                </OverlayTrigger>
                            </Form.Label>

                            <InputGroup>
                                <Form.Control
                                    type="password"
                                    name="password"
                                    placeholder="Create password"
                                    value={newUser.password}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.password}
                                />
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="generate-password-tooltip">
                                        Generate a secure random password
                                        </Tooltip>
                                    }
                                >
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleGeneratePassword}
                                        className="d-flex align-items-center"
                                    >
                                        <FontAwesomeIcon icon={faSyncAlt} />
                                    </Button>
                                </OverlayTrigger>
                            </InputGroup>

                            <Form.Control.Feedback type="invalid">
                                {formErrors.password}
                            </Form.Control.Feedback>

                            {generatedPassword && (
                                <Alert variant="info" className="mt-2">
                                    Generated password: <strong>{generatedPassword}</strong>
                                </Alert>
                            )}

                            <Form.Text muted>
                                Must be 12-128 characters with at least 3 of: uppercase, lowercase, number, and special character.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control
                                type="password"
                                name="confirmPassword"
                                placeholder="Confirm password"
                                value={newUser.confirmPassword}
                                onChange={handleInputChange}
                                isInvalid={!!formErrors.confirmPassword}
                            />
                            <Form.Control.Feedback type="invalid"> {formErrors.confirmPassword} </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                name="role"
                                value={newUser.role}
                                onChange={handleInputChange}
                            >
                                <option value="admin">Admin</option>
                                <option value="user">User</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>  Cancel </Button>
                    <Button variant="primary" onClick={handleCreateUser}> Create User </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUser && (
                        <>
                            <p>Are you sure you want to delete the following user?</p>
                            <div className="bg-light p-3 rounded">
                                <div><strong>Name:</strong> {selectedUser.name || 'N/A'}</div>
                                <div><strong>E-mail:</strong> {selectedUser.email}</div>
                                <div><strong>Role:</strong> {renderRoleBadge(selectedUser.role)}</div>
                            </div>
                            <Alert variant="danger" className="mt-3">
                                <FontAwesomeIcon icon={faLock} className="me-2" />
                                This action cannot be undone. All user data will be permanently removed.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}> Cancel </Button>
                    <Button variant="danger" onClick={handleDeleteUser}> Delete User </Button>
                </Modal.Footer>
            </Modal>

            {/* Reset Password Confirmation Modal */}
            <Modal show={showResetPasswordModal} onHide={() => setShowResetPasswordModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Password Reset</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUser && (
                        <>
                            <p>Are you sure you want to reset the password for the following user?</p>
                            <div className="bg-light p-3 rounded">
                                <div><strong>Name:</strong> {selectedUser.name || 'N/A'}</div>
                                <div><strong>E-mail:</strong> {selectedUser.email}</div>
                                <div><strong>Role:</strong> {renderRoleBadge(selectedUser.role)}</div>
                            </div>
                            <Alert variant="warning" className="mt-3">
                                <FontAwesomeIcon icon={faLock} className="me-2" />
                                This will generate a new random password for the user. The old password will no longer work.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)}> Cancel </Button>
                    <Button variant="warning" onClick={handleResetPassword}> Reset Password </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserControl;